import { useIntl } from '@/intl';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { horizontalGap, Icon, SpaceBetween, tk, Typography, useToken, zIndex } from '@sunwebgroup/ui-components';
import { AnchorHref } from './types';
import type { EmblaCarouselType } from 'embla-carousel/components/EmblaCarousel';
import { usePDPSelector, usePDPStore } from '@/scopes/products/infrastructure/usePDPStore';
import { useAnchorMenu } from './hooks';

const menuItems: Readonly<{ label: string; anchorHref: AnchorHref }[]> = [
  { label: 'accomodation_anchor_menu_general', anchorHref: AnchorHref.General },
  {
    label: 'accomodation_anchor_menu_roomtypes',
    anchorHref: AnchorHref.Roomtypes,
  },
  {
    label: 'accomodation_anchor_menu_facilities',
    anchorHref: AnchorHref.Facilities,
  },
  {
    label: 'accomodation_anchor_menu_practical_info',
    anchorHref: AnchorHref.PracticalInfo,
  },
  {
    label: 'accomodation_anchor_menu_reviews',
    anchorHref: AnchorHref.Reviews,
  },
  {
    label: 'accomodation_anchor_menu_destination',
    anchorHref: AnchorHref.Destination,
  },
] as const;

const Wrapper = styled(SpaceBetween)`
  border-bottom: 2px solid ${tk('color_semantic_border_subdued')};
  position: sticky;
  top: 0;
  background-color: white;
  z-index: ${zIndex.anchorMenu};
`;

const Slider = styled.nav`
  width: 100%;
  overflow: hidden;
  margin-bottom: -2px;
`;

const MenuContainer = styled.ul`
  margin: 0;
  list-style: none;
  padding-left: unset;
  display: flex;
  ${horizontalGap('spacing_lg')}
`;

const MenuItem = styled.li`
  white-space: nowrap;
  user-select: none;

  &:first-of-type {
    padding-left: 0;
  }

  &:last-of-type {
    padding-right: 0;
  }
`;

const MenuItemLink = styled.div`
  padding: ${tk('spacing_sm')} 0;
  text-decoration: none;
  cursor: pointer;

  &:hover,
  &.active {
    color: ${tk('color_semantic_text_primary')};
    border-bottom: 2px solid ${tk('color_semantic_border_primary')};
  }
`;

export default function AnchorMenu() {
  const intl = useIntl();
  const store = usePDPStore();
  const activeAnchorHref = usePDPSelector((state) => state.activeAnchorHref);
  const setActiveAnchorHref = store.setActiveAnchorHref;
  // TODO: Refactor to use useAnalytics hook for everything in the click event
  const { handleAnchorClick: sendClickEvent } = useAnchorMenu();

  const handleAnchorClick = (anchor: AnchorHref) => {
    document.getElementById(anchor)?.scrollIntoView({
      behavior: 'smooth',
    });
    setActiveAnchorHref(anchor);
    sendClickEvent(anchor);
  };

  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
    breakpoints: {
      [`${useToken<string>('mq_tablet')}`]: { active: false },
    },
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const showScrollButtons = useShowScrollButton(emblaApi);

  return (
    <Wrapper direction="x" spacing={tk('spacing_xs')} alignItems="baseline">
      {showScrollButtons && <Icon glyph="IconChevronLeft" onClick={scrollPrev} size="xs" cursor="pointer" />}
      <Slider ref={emblaRef}>
        <MenuContainer>
          {menuItems.map((item) => (
            <MenuItem key={item.anchorHref}>
              <MenuItemLink
                className={activeAnchorHref === item.anchorHref ? 'active' : undefined}
                onClick={() => handleAnchorClick(item.anchorHref)}
              >
                <Typography $variant="body-lg">{intl.formatMessage({ id: item.label })}</Typography>
              </MenuItemLink>
            </MenuItem>
          ))}
        </MenuContainer>
      </Slider>
      {showScrollButtons && <Icon glyph="IconChevronRight" onClick={scrollNext} size="xs" cursor="pointer" />}
    </Wrapper>
  );
}

function useShowScrollButton(emblaApi?: EmblaCarouselType) {
  const [showScrollButtons, setShowScrollButtons] = useState(false);

  useEffect(() => {
    emblaApi?.on('slidesInView', (a) => {
      setShowScrollButtons(Boolean(a.slidesNotInView()?.length));
    });
  }, [emblaApi]);

  return showScrollButtons;
}
